import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { History, Eye, Upload, Trash2, BookmarkPlus, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Seoul', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) + ' KST' : 'Built-in';

export const FloorHistoryPanel = ({ token, refreshKey, onPreview, onRestored, previewId }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [versions, setVersions] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(() => axios.get(`${API}/admin/floor/plan/history`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => setVersions(r.data.versions)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  const saveVersion = async () => {
    setBusy('save');
    try {
      await axios.post(`${API}/admin/floor/plan/history`, { name }, { headers });
      setName(''); await load(); flash('Version saved');
    } catch { flash('Could not save version'); }
    finally { setBusy(''); }
  };
  const preview = async (v) => {
    setBusy(v.id);
    try {
      const { data } = await axios.get(`${API}/admin/floor/plan/history/${v.id}`, { headers });
      onPreview(data.plan, v);
    } finally { setBusy(''); }
  };
  const restore = async (v) => {
    if (!window.confirm(`Apply "${v.name}" to the live billboard? The current layout is kept in history.`)) return;
    setBusy(v.id);
    try {
      const { data } = await axios.post(`${API}/admin/floor/plan/history/${v.id}/restore`, {}, { headers });
      onRestored(data); await load(); flash('Layout restored');
    } catch { flash('Restore failed'); }
    finally { setBusy(''); }
  };
  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.name}" from history?`)) return;
    await axios.delete(`${API}/admin/floor/plan/history/${v.id}`, { headers });
    load();
  };

  return (
    <div className="bg-background-surface border border-white/[0.06] rounded-xl p-5" data-testid="floor-history-panel">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="font-heading text-sm tracking-wider uppercase text-primary-main flex items-center gap-2 flex-1"><History className="w-4 h-4" /> Layout history</h3>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="Name this layout (e.g. Festival Oct 2026)" className="w-72 h-9 bg-background-base border-white/10 text-white" data-testid="floor-history-name-input" />
        <Button size="sm" onClick={saveVersion} disabled={busy === 'save'} className="bg-secondary-main text-black hover:opacity-90 font-heading tracking-wider" data-testid="floor-history-save-btn">
          <BookmarkPlus className="w-4 h-4 mr-1.5" /> Save current as version
        </Button>
        {msg && <span className="text-xs text-secondary-main" data-testid="floor-history-msg">{msg}</span>}
      </div>
      <p className="text-[11px] text-white/35 mb-3">Every “Save layout”, reset or restore keeps a backup here automatically. <b>Preview</b> loads a version into the editor above (nothing changes until you press Save layout). <b>Apply</b> puts it on the billboard right away.</p>
      <ul className="divide-y divide-white/[0.05] max-h-80 overflow-auto" data-testid="floor-history-list">
        {versions.map(v => (
          <li key={v.id} className={`flex flex-wrap items-center gap-3 py-2 ${previewId === v.id ? 'bg-secondary-main/10 -mx-2 px-2 rounded-md' : ''}`} data-testid={`floor-history-row-${v.id}`}>
            <div className="flex-1 min-w-[220px]">
              <p className="text-sm text-white flex items-center gap-2" data-testid={`floor-history-name-${v.id}`}>
                {v.builtin && <Lock className="w-3 h-3 text-white/40" />}{v.name}
                {v.auto && <span className="text-[9px] uppercase tracking-wider text-white/40 border border-white/15 rounded px-1">auto</span>}
              </p>
              <p className="text-[11px] text-white/40">{fmt(v.created_at)} · {v.tables_count} tables · {v.table_style}{v.has_bg ? ' · background' : ''}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => preview(v)} disabled={busy === v.id} className="border border-white/10 text-white/70 h-8" data-testid={`floor-history-preview-${v.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button>
            <Button variant="ghost" size="sm" onClick={() => restore(v)} disabled={busy === v.id} className="border border-primary-main/40 text-primary-main hover:bg-primary-main/10 h-8" data-testid={`floor-history-restore-${v.id}`}><Upload className="w-3.5 h-3.5 mr-1" /> Apply</Button>
            {!v.builtin && <button onClick={() => remove(v)} className="text-white/30 hover:text-red-400" data-testid={`floor-history-delete-${v.id}`}><Trash2 className="w-4 h-4" /></button>}
          </li>
        ))}
        {versions.length === 0 && <li className="text-white/40 text-sm py-2">No saved versions yet.</li>}
      </ul>
    </div>
  );
};
